import { isAuthError, isAuthRetryableFetchError, isAuthWeakPasswordError } from '@supabase/supabase-js';

export type ErrorCode = 'network' | 'validation' | 'td_api' | 'supabase' | 'conflict' | 'unknown';

export class AppError extends Error {
  constructor(
    message: string,
    public readonly code: ErrorCode,
    public readonly userMessage: string
  ) {
    super(message);
    this.name = 'AppError';
  }
}

export class ConflictError extends AppError {
  constructor() {
    super(
      'Conflict: row was modified by another user',
      'conflict',
      'Données modifiées par un autre utilisateur. Veuillez rafraîchir la page.'
    );
    this.name = 'ConflictError';
  }
}

// Panne d'envoi d'un email Supabase Auth (SMTP refuse, timeout). GoTrue repond 200 pour
// un email inconnu (anti-enumeration, USR-R15) : un `error` retourne par
// resetPasswordForEmail concerne donc toujours un compte existant.
export class EmailSendError extends AppError {
  constructor(technical: string) {
    super(
      technical,
      'supabase',
      "L'email n'a pas pu être envoyé (service email indisponible). Le compte existe : utilisez « Renvoyer l'invitation » plus tard."
    );
    this.name = 'EmailSendError';
  }
}

// Limite d'envoi GoTrue (`over_email_send_rate_limit`) : un lien par adresse par minute
// (max_frequency) ou quota horaire du projet (email_sent). N'est renvoyee que pour un
// compte existant : la page publique DOIT la traiter comme un succes neutre (USR-R15),
// seul l'admin (qui connait l'email) voit ce message.
export class EmailRateLimitError extends AppError {
  constructor(technical: string) {
    super(
      technical,
      'supabase',
      "Un lien a déjà été envoyé à cette adresse il y a moins d'une minute (ou le quota horaire d'envoi est atteint). Patientez avant de renvoyer."
    );
    this.name = 'EmailRateLimitError';
  }
}

// Re-exports de compatibilite — bloc D-V1D-01 Pennylane deplace lot 4 boilerplate
// (decontamination du socle) vers services/integrations/pennylaneErrors.ts.
// Cycle de modules benin et assume : pennylaneErrors importe extractEdgeFunctionBody
// d'ici (usage a l'appel uniquement, jamais a l'init de module).
export {
  PennylaneSyncFailureError,
  detectPennylaneSyncFailure,
  type PennylaneSyncFailureDetails,
} from '@/services/integrations/pennylaneErrors';

// Codes metier remontes par les DB functions atomiques via RAISE EXCEPTION 'CODE'.
// Source de verite partagee entre les migrations SQL et le front.
export const RPC_ERROR_CODES = {
  CONFLICT: 'CONFLICT',
  BSD_ACTIFS: 'BSD_ACTIFS',
  TRANSPORT_ACTIF: 'TRANSPORT_ACTIF',
  CAP_BSD_ACTIFS: 'CAP_BSD_ACTIFS',
  CAP_TYPE_BSD_ACTIF: 'CAP_TYPE_BSD_ACTIF',
  NOT_BROUILLON: 'NOT_BROUILLON',
  MARCHANDISE_INCONNUE: 'MARCHANDISE_INCONNUE',
  // TR-R41, TR-R14 (Lot 14a) : chaine de transports d'un BSD. Message SQL « CODE : detail ».
  SUITE_ACTIVE: 'SUITE_ACTIVE',
  RANG_MAX: 'RANG_MAX',
  BSD_DEJA_TRANSPORTE: 'BSD_DEJA_TRANSPORTE',
  // RC3.115 (BSD-R08) : BSD deja pris en charge sur TD, refuse au 1er trajet. Message SQL « CODE : numero ».
  BSD_PRIS_EN_CHARGE: 'BSD_PRIS_EN_CHARGE',
  // D-MODIF-DEMANDE-ADMIN (bloc 3) : retrait d'un BSD par « Modifier la demande ». Message SQL « CODE : detail ».
  BSD_NON_RETIRABLE: 'BSD_NON_RETIRABLE',
  BSD_SUITE_ACTIVE: 'BSD_SUITE_ACTIVE',
  DERNIER_BSD_TRANSPORT: 'DERNIER_BSD_TRANSPORT',
  // Bloc 4 (M12) : livraison videe et gardee en commande, livraison supprimee qui porte un BSD, plus aucune livraison.
  LIVRAISON_SANS_BSD: 'LIVRAISON_SANS_BSD',
  LIVRAISON_AVEC_BSD: 'LIVRAISON_AVEC_BSD',
  DERNIERE_LIVRAISON: 'DERNIERE_LIVRAISON',
  // R1015-20 : BSD ajoute rattache a un autre transport entre-temps (pas un verrou perime).
  BSD_INDISPONIBLE: 'BSD_INDISPONIBLE',
  // D-BSD-CHANGER-DECHET (RC4.22) : changer_dechet_bsd.
  BSD_INTROUVABLE: 'BSD_INTROUVABLE',
  ORIGINE_NON_ELIGIBLE: 'ORIGINE_NON_ELIGIBLE',
  DECHET_NON_ELIGIBLE: 'DECHET_NON_ELIGIBLE',
  REGIME_INCOMPATIBLE: 'REGIME_INCOMPATIBLE',
  BSD_SUR_TRANSPORT: 'BSD_SUR_TRANSPORT',
  BSD_SUR_FACTURE: 'BSD_SUR_FACTURE',
  REEXPEDITION_ENGAGEE: 'REEXPEDITION_ENGAGEE',
} as const;

export type RpcErrorCode = typeof RPC_ERROR_CODES[keyof typeof RPC_ERROR_CODES];

const RPC_BUSINESS_MESSAGES: Record<RpcErrorCode, string> = {
  CONFLICT: 'Données modifiées par un autre utilisateur. Veuillez rafraîchir la page.',
  BSD_ACTIFS: 'Opération impossible : des BSD non finalisés sont rattachés à ce chantier. Annulez-les ou clôturez-les sur Trackdechets d\'abord.',
  TRANSPORT_ACTIF: 'Opération impossible : des transports actifs sont rattachés à ce chantier. Annulez ou terminez-les d\'abord.',
  CAP_BSD_ACTIFS: 'Impossible d\'annuler ce CAP : des BSD actifs y sont rattachés. Annulez-les ou clôturez-les sur Trackdechets d\'abord.',
  CAP_TYPE_BSD_ACTIF: 'Type non modifiable : un BSD existe pour ce déchet. Supprimez-le ou annulez-le d\'abord.',
  NOT_BROUILLON: 'Suppression possible uniquement en statut BROUILLON.',
  MARCHANDISE_INCONNUE: 'Au moins une marchandise est introuvable. Rafraîchissez la page et réessayez.',
  SUITE_ACTIVE: "Annulation impossible : ce transport a un transport suivant. Annulez-le d'abord ; s'il est en brouillon, le client doit le supprimer.",
  RANG_MAX: 'Ce BSD a déjà son nombre maximal de trajets.',
  BSD_DEJA_TRANSPORTE: "Ce BSD est déjà livré à son centre : il n'a pas de transport suivant.",
  BSD_PRIS_EN_CHARGE: 'Un BSD est déjà pris en charge sur Trackdéchets : il ne peut plus entrer dans un transport.',
  BSD_NON_RETIRABLE: 'Un BSD a déjà été pris en charge par le transporteur : il ne peut plus quitter ce transport.',
  BSD_SUITE_ACTIVE: 'Un BSD a un transport suivant : il ne peut pas quitter ce transport.',
  DERNIER_BSD_TRANSPORT: 'Le transport doit garder au moins un BSD : pour tout retirer, annulez le transport.',
  LIVRAISON_SANS_BSD: "Une livraison n'a plus de BSD : supprimez-la ou rangez-y un BSD.",
  LIVRAISON_AVEC_BSD: "Une livraison à supprimer porte encore un BSD : décochez-le à l'étape 2.",
  DERNIERE_LIVRAISON: "Le transport doit garder au moins une livraison : pour tout retirer, annulez le transport.",
  BSD_INDISPONIBLE: "Un BSD ajouté n'est plus disponible (rattaché à un autre transport entre-temps) : décochez-le et réessayez.",
  BSD_INTROUVABLE: "Ce BSD n'existe plus.",
  ORIGINE_NON_ELIGIBLE: "Un BSD créé par l'application ne change pas de déchet : annulez-le sur Trackdéchets et créez-en un sur le bon déchet.",
  DECHET_NON_ELIGIBLE: 'Ce déchet ne peut pas recevoir de BSD (annulé).',
  REGIME_INCOMPATIBLE: "Ce déchet n'est pas du même régime que le BSD (BSDA ou BSDD) : choisissez un déchet du même régime.",
  BSD_SUR_TRANSPORT: 'Ce BSD est sur un transport : retirez-le du transport avant de changer de déchet.',
  BSD_SUR_FACTURE: 'Ce BSD est sur une facture : il ne peut plus changer de déchet.',
  REEXPEDITION_ENGAGEE: 'La réexpédition de ce BSD est sur un transport ou une facture : il ne peut plus changer de déchet.',
};

// A appeler dans le catch des appels supabase.rpc(...).
export function handleRpcError(error: { message: string; code?: string }): never {
  // Message SQL « CODE » ou « CODE : detail » (codes de la chaine de transports, Lot 14a).
  const [prefixe, ...reste] = error.message.split(' : ');
  const code = prefixe as RpcErrorCode;
  const detail = reste.join(' : ');
  if (code === RPC_ERROR_CODES.CONFLICT && !detail) throw new ConflictError();
  if (code === RPC_ERROR_CODES.SUITE_ACTIVE && detail) {
    throw new AppError(error.message, 'validation',
      `Annulation impossible : ${detail}. Annulez-le d'abord ; s'il est en brouillon, le client doit le supprimer.`);
  }
  if (code === RPC_ERROR_CODES.BSD_PRIS_EN_CHARGE && detail) {
    throw new AppError(error.message, 'validation',
      `Le BSD ${detail} est déjà pris en charge sur Trackdéchets : il ne peut plus entrer dans un transport.`);
  }
  if (code === RPC_ERROR_CODES.BSD_NON_RETIRABLE && detail) {
    throw new AppError(error.message, 'validation',
      `Le BSD ${detail} a déjà été pris en charge par le transporteur : il ne peut plus quitter ce transport.`);
  }
  if (code === RPC_ERROR_CODES.BSD_SUITE_ACTIVE && detail) {
    // Detail « numero (reference du transport suivant) ».
    const [, numero = detail, suivant = '-'] = /^(.*) \((.*)\)$/.exec(detail) ?? [];
    throw new AppError(error.message, 'validation',
      `Le BSD ${numero} a un transport suivant (${suivant}) : il ne peut pas quitter ce transport.`);
  }
  if (code === RPC_ERROR_CODES.LIVRAISON_SANS_BSD && detail) {
    throw new AppError(error.message, 'validation', `La livraison ${detail} n'a plus de BSD : supprimez-la ou rangez-y un BSD.`);
  }
  if (code === RPC_ERROR_CODES.LIVRAISON_AVEC_BSD && detail) {
    throw new AppError(error.message, 'validation', `La livraison ${detail} porte encore un BSD : décochez-le à l'étape 2 avant de la supprimer.`);
  }
  if (code in RPC_BUSINESS_MESSAGES) {
    throw new AppError(error.message, 'validation', RPC_BUSINESS_MESSAGES[code]);
  }
  handleSupabaseError(error);
}

// Codes PostgreSQL courants → messages utilisateur
const PG_MESSAGES: Record<string, string> = {
  '42501': "Vous n'avez pas les droits pour cette action.",
  '23505': 'Cet élément existe déjà.',
  '23503': 'Impossible : des données liées existent encore.',
  '23514': 'Données invalides (contrainte non respectée).',
  'PGRST301': 'Aucun résultat trouvé.',
};

export function handleSupabaseError(error: { message: string; code?: string }): never {
  const userMessage = (error.code && PG_MESSAGES[error.code])
    ?? 'Erreur de base de données. Réessayez.';
  throw new AppError(error.message, 'supabase', userMessage);
}

export function handleTdError(technicalMessage: string): never {
  const isNetwork = technicalMessage.includes('FetchError')
    || technicalMessage.includes('network')
    || technicalMessage.includes('ECONNREFUSED')
    || technicalMessage.includes('timeout');

  const userMessage = isNetwork
    ? 'Trackdechets est temporairement indisponible. Réessayez dans quelques instants.'
    : `Erreur Trackdechets : ${technicalMessage}`;

  throw new AppError(technicalMessage, isNetwork ? 'network' : 'td_api', userMessage);
}

// Messages de fetch en echec : Chrome, Firefox, Safari (postgrest-js les prefixe par "TypeError: ").
const RESEAU = /Failed to fetch|NetworkError|Load failed/;

// Teste le message brut : getUserMessage le traduit, le filet de queryClient ne pourrait plus le reconnaitre.
export function isNetworkError(error: unknown): boolean {
  if (error instanceof AppError) return error.code === 'network';
  const message = (error as { message?: unknown } | null)?.message;
  return typeof message === 'string' && RESEAU.test(message);
}

// `.single()` sans ligne (id inexistant, supprime ou hors tenant). Avec plusieurs lignes, PGRST116 est un
// vrai bug : son message technique est garde.
export function isNotFoundError(error: unknown): boolean {
  const { code, details } = (error ?? {}) as { code?: unknown; details?: unknown };
  return code === 'PGRST116' && details === 'The result contains 0 rows';
}

export function getUserMessage(err: unknown): string {
  if (err instanceof AppError) return err.userMessage;
  // Erreurs PostgREST sans texte lisible (objet brut ou PostgrestError, qui est une Error).
  if (isNetworkError(err)) return NETWORK_ERROR_MESSAGE;
  if (isNotFoundError(err)) return 'Élément introuvable : il a peut-être été supprimé.';
  if (err instanceof Error) return err.message;
  // PostgrestError (Supabase) et autres objets porteurs d'un champ `message`
  // ne sont pas des instances de Error : remonter quand meme leur message
  // pour que le toast affiche le RAISE EXCEPTION du trigger plutot qu'un
  // fallback generique.
  if (typeof err === 'object' && err !== null && 'message' in err) {
    const message = (err as { message: unknown }).message;
    if (typeof message === 'string' && message.trim() !== '') return message;
  }
  return 'Erreur inconnue';
}

/**
 * Chantier : -
 * Regle metier : USR-R14, USR-R15 (PRODUIT/domaine/15_autorisations.md)
 * Decision : D-AUTH-06 (PRODUIT/decisions/)
 * Message en francais pour une erreur Supabase Auth, jamais le message anglais brut. Les regles de
 * mot de passe sont deja verifiees cote client (lib/passwordPolicy.ts) : un `weak_password` serveur
 * signifie en pratique un mot de passe compromis (verification HIBP).
 */
export function authErrorMessage(err: unknown): string {
  if (isAuthRetryableFetchError(err)) return NETWORK_ERROR_MESSAGE;
  if (isAuthWeakPasswordError(err)) {
    return 'Ce mot de passe figure dans des fuites de données connues : choisissez-en un autre.';
  }
  if (isAuthError(err) && err.code === 'same_password') {
    return "Le nouveau mot de passe doit être différent de l'ancien.";
  }
  // Code releve dans les logs Auth (recette Dev du 2026-09-29), absent de la doc Supabase.
  if (isAuthError(err) && err.code === 'current_password_invalid') {
    return 'Mot de passe actuel incorrect.';
  }
  // secure_password_change (config.toml) : changement refuse si la session a plus de 24 h.
  if (isAuthError(err) && err.code === 'reauthentication_needed') {
    return 'Par sécurité, déconnectez-vous puis reconnectez-vous avant de changer votre mot de passe.';
  }
  return 'Une erreur est survenue. Réessayez dans quelques instants.';
}

export const NETWORK_ERROR_MESSAGE =
  'Connexion au serveur impossible : vérifiez votre réseau ou le pare-feu de votre entreprise.';

// Extrait le message d'erreur depuis le body d'une Response attachee a FunctionsHttpError.
// Fallback sur error.message si le body n'est pas parseable.
export async function extractEdgeFunctionError(error: unknown): Promise<string> {
  const err = error as { message?: string; context?: Response };
  try {
    const ctx = err?.context;
    if (ctx && typeof ctx.clone === 'function') {
      const body = await ctx.clone().json();
      if (body?.error) return body.error;
    }
  } catch { /* ignore */ }
  return err?.message ?? 'Erreur inconnue';
}

// Extrait le body JSON complet d'une Response attachee a FunctionsHttpError (null si non parseable).
// Utilise pour recuperer les reponses structurees (ex: {action:'retry_bdd_update', ...} sur 500).
export async function extractEdgeFunctionBody(error: unknown): Promise<Record<string, unknown> | null> {
  const err = error as { context?: Response };
  try {
    const ctx = err?.context;
    if (ctx && typeof ctx.clone === 'function') {
      return await ctx.clone().json() as Record<string, unknown>;
    }
  } catch { /* ignore */ }
  return null;
}
